import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '../../../components';
import { amountByCurrency } from './refund-format';

/**
 * Step 1 of a dispute resolution — the option chooser. Generic across dispute
 * types: the caller passes the `prompt` and `options`. Next is disabled until
 * an option is selected.
 */
const ResolutionChooseStep = ({ dispute, options, prompt, selectedId, onSelect, onNext }) => {
  // The fiat/crypto excess (e.g. NGN 300,000) is distinct from the crypto txn
  // amount in the table. Prefer the dedicated excess fields when present.
  const excess = amountByCurrency(
    dispute?.excess_amount ?? dispute?.amount,
    dispute?.excess_currency ?? dispute?.currency_slug
  );

  const describe = (option) => {
    if (option.id === 'refund_excess') {
      return (
        <>
          We will refund <strong>{excess}</strong> to your customer and they will bear the refund
          fee.
        </>
      );
    }
    return option.description;
  };

  return (
    <div className="resolve resolve--choose">
      <p className="resolve__subtitle">
        Settlement value is calculated using the current exchange rate at acceptance time.
      </p>
      <p className="resolve__prompt">{prompt}</p>

      <div className="resolve__options" role="radiogroup" aria-label="Resolution options">
        {options.map((option) => {
          const checked = selectedId === option.id;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={checked}
              key={option.id}
              data-testid={`resolve-option-${option.id}`}
              className={`resolve__option${checked ? ' resolve__option--selected' : ''}`}
              onClick={() => onSelect(option.id)}
            >
              <span className={`resolve__radio${checked ? ' resolve__radio--on' : ''}`} />
              <span className="resolve__option-text">
                <span className="resolve__option-label">{option.label}</span>
                <span className="resolve__option-desc">{describe(option)}</span>
              </span>
            </button>
          );
        })}
      </div>

      <Button isFull disabled={!selectedId} onClick={onNext} data-testid="resolve-next-btn">
        Next
      </Button>
    </div>
  );
};

ResolutionChooseStep.propTypes = {
  dispute: PropTypes.object,
  options: PropTypes.array.isRequired,
  prompt: PropTypes.string.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

export { ResolutionChooseStep };
